
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Client } from "@/types";
import { transformClientData } from "@/utils/clientTransformUtils";
import { calculateClientMetrics } from "@/utils/clientMetricsUtils";
import { fetchClientData, fetchClientPrograms, getCurrentUser } from "@/services/client/clientService";

export function useClientDetail(clientId: string | undefined) {
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const loadClientData = async () => {
      if (!clientId) {
        setError("No client ID provided");
        setIsLoading(false);
        return;
      }

      try {
        // Get the current user
        const user = await getCurrentUser();
        
        // Fetch the client from Supabase
        // Updated to pass only clientId, assuming fetchClientData now handles user context internally
        const supabaseClient = await fetchClientData(clientId);
        
        // Now, fetch all programs for this client to get accurate metrics
        const programsData = await fetchClientPrograms(clientId);
        
        console.log("Programs data for client:", programsData);
        
        // Calculate attendance and other metrics
        const metrics = calculateClientMetrics(programsData);
        
        // Transform the Supabase data to match our Client type
        const transformedClient = transformClientData(
          supabaseClient,
          metrics,
          programsData
        );

        setClient(transformedClient);
      } catch (err: any) {
        console.error("Error in loadClientData:", err);
        setError(err.message || "An error occurred while fetching client data");
      } finally {
        setIsLoading(false);
      }
    };

    loadClientData();
  }, [clientId, toast]);

  return { isLoading, client, error };
}
